import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import styled from "styled-components";
import { Badge, Box, Stack } from "@mui/material";
import useRelatedDocuments from "../hooks/useRelatedDocuments";
import forEach from "lodash/forEach";
import map from "lodash/map";
import size from "lodash/size";
import CheckIcon from "@mui/icons-material/Check";
import { useDropzone } from "react-dropzone";
import {
  extractInformationFromFile,
  getExtensionFromFilename,
} from "../../helpers/utils";
import DocumentsService from "../../services/DocumentsService";

const VisuallyHiddenInput = styled.input({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const UploadButton = ({
  label = "Uploader un fichier",
  register,
  estateId,
  setValue,
  defaultSelectedDoc,
  ...props
}) => {
  const [selectedDoc, setSelectedDoc] = useState();

  const { data: relatedDocuments, refetch } = useRelatedDocuments({
    estateId,
    documentTypes: [28],
    withContent: true,
  });

  const onSelectDocument = (docId, base64) => {
    setSelectedDoc(docId);
    setValue("sponsorLogo", base64);
  };

  const handleUpload = async (droppedFiles) => {
    const formattedFiles = [];
    const associations = {};
    forEach(droppedFiles, (file) => {
      const uuid = crypto.randomUUID();
      associations[uuid] = [parseInt(estateId)];
      formattedFiles.push({
        uuid,
        content: file,
        ...extractInformationFromFile(file),
        documentType: 28,
      });
    });

    try {
      await DocumentsService.uploadDocuments(formattedFiles, associations);
      refetch();
    } catch (error) {
      console.error("Error uploading files:", error);
    }
  };

  const { getInputProps } = useDropzone({
    onDrop: handleUpload,
    accept: {
      "image/png": [".png", ".jpg", ".jpeg"],
      "application/pdf": [".pdf"],
    },
  });

  const buildBase64Src = (extension, content) =>
    `data:image/${extension};base64,${content}`;

  useEffect(() => {
    if (!!defaultSelectedDoc) {
      setSelectedDoc(defaultSelectedDoc);
    }
  }, [defaultSelectedDoc]);

  useEffect(() => {
    if (!!selectedDoc) {
      setValue("project.sponsorLogoId", selectedDoc);
    }
  }, [selectedDoc]);

  useEffect(() => {
    if (!!relatedDocuments && size(relatedDocuments) > 0 && !selectedDoc) {
      const firstDoc = relatedDocuments[0];
      const extension = getExtensionFromFilename(firstDoc.filename);
      const base64 = buildBase64Src(extension, firstDoc.content);
      setSelectedDoc(firstDoc.id);
      setValue("sponsorLogo", base64);
    } else if (!!selectedDoc) {
      const selectedDocument = relatedDocuments.find(
        (doc) => doc.id === selectedDoc
      );
      if (!selectedDocument) return;
      const extension = getExtensionFromFilename(selectedDocument.filename);
      const base64 = buildBase64Src(extension, selectedDocument.content);
      setSelectedDoc(selectedDocument.id);
      setValue("sponsorLogo", base64);
    }
  }, [relatedDocuments]);

  return (
    <>
      <Button
        component="label"
        role={undefined}
        variant="contained"
        tabIndex={-1}
        startIcon={<CloudUploadIcon />}
        {...props}
      >
        {label}
        <VisuallyHiddenInput
          type="file"
          id="file-upload"
          {...getInputProps()}
        />
      </Button>
      <Stack flexDirection="row" sx={{ gap: 2, marginTop: 2 }} flexWrap="wrap">
        {map(relatedDocuments, (doc) => {
          const filename = doc.filename;
          const extension = getExtensionFromFilename(filename);
          const base64 = buildBase64Src(extension, doc.content);
          const isSelected = selectedDoc && selectedDoc === doc.id;
          return (
            <Badge
              showZero={false}
              badgeContent={isSelected ? <CheckIcon fontSize="small" /> : 0}
              slotProps={{
                badge: {
                  sx: {
                    color: "var(--white)",
                    backgroundColor: "var(--green-color)",
                    width: 20,
                    height: 20,
                  },
                },
              }}
            >
              <Box
                key={doc.id}
                role="button"
                onClick={() => onSelectDocument(doc.id, base64)}
                sx={{
                  width: 150,
                  height: 100,
                  borderRadius: 1,
                  overflow: "hidden",
                  p: 1,
                  backgroundColor: "var(--white)",
                  boxShadow: "0px 0px 10px -8px rgba(130, 130, 130, 1)",
                }}
              >
                <img
                  src={base64}
                  alt={filename}
                  style={{
                    objectFit: "contain",
                    width: "100%",
                    height: "100%",
                  }}
                />
              </Box>
            </Badge>
          );
        })}
      </Stack>
    </>
  );
};

export default UploadButton;
